// The one saved set of trading presets (operator 2026-10-12: "one preset set
// for everything"). The rail's form, Instant trade, a list's Quick buy and a
// new limit order all read the slippage of preset 1–3 from here, so an edit in
// the Trade settings dialog reaches every one of them, on this tab and the
// others, and is still there after a reload.
//
// A preset is what lib/tradePanel says it is — buy slippage, sell slippage and
// the cap, in basis points. The storage is the truth: every read goes to it and
// is checked again, so a value changed under the page (another tab, a cleared
// storage, a hand edit) is never trusted and never stale. Whatever is there,
// what leaves is three presets of three whole figures between 0 and what the
// server takes (MAX_SLIPPAGE_BPS — the bounds the editor has always held a
// field to); a field outside them is the shipped default of that field, and a
// list that is not three presets is the shipped set. With nothing saved the
// shipped set itself is returned, so nothing about a request changes until the
// user changes a preset.

import { useSyncExternalStore } from 'react'
import { DEFAULT_TRADE_PRESETS, MAX_SLIPPAGE_BPS, type TradePreset } from './tradePanel'

export const TRADE_PRESETS_KEY = 'wils.trade.presets.v1'

const listeners = new Set<() => void>()

// The last text read from the storage and the set it parsed to: the same text
// is the same set (useSyncExternalStore needs a snapshot that holds still).
let seenRaw: string | null = null
let seenSet: readonly TradePreset[] = DEFAULT_TRADE_PRESETS

// A storage that refuses a write (private mode, a full quota) must not lose the
// edit for the session: the text is held here until a write goes through.
let unsaved: { raw: string | null } | null = null

function validBps(value: unknown): value is number {
  return (
    typeof value === 'number' && Number.isInteger(value) && value >= 0 && value <= MAX_SLIPPAGE_BPS
  )
}

function checked(saved: unknown): readonly TradePreset[] {
  if (!Array.isArray(saved) || saved.length !== DEFAULT_TRADE_PRESETS.length) {
    return DEFAULT_TRADE_PRESETS
  }
  return DEFAULT_TRADE_PRESETS.map((shipped, index) => {
    const row: unknown = saved[index]
    const fields = typeof row === 'object' && row !== null ? (row as Record<string, unknown>) : {}
    const field = (name: keyof TradePreset) => {
      const value = fields[name]
      return validBps(value) ? value : shipped[name]
    }
    return {
      buySlippageBps: field('buySlippageBps'),
      sellSlippageBps: field('sellSlippageBps'),
      maxSlippageBps: field('maxSlippageBps'),
    }
  })
}

function parsed(raw: string | null): readonly TradePreset[] {
  if (raw === null) return DEFAULT_TRADE_PRESETS
  try {
    return checked(JSON.parse(raw))
  } catch {
    return DEFAULT_TRADE_PRESETS
  }
}

function storedRaw(): string | null {
  if (unsaved) return unsaved.raw
  try {
    return localStorage.getItem(TRADE_PRESETS_KEY)
  } catch {
    return null
  }
}

function store(raw: string | null) {
  try {
    if (raw === null) localStorage.removeItem(TRADE_PRESETS_KEY)
    else localStorage.setItem(TRADE_PRESETS_KEY, raw)
    unsaved = null
  } catch {
    unsaved = { raw }
  }
  for (const listener of [...listeners]) listener()
}

/** The three presets, as saved; the shipped ones where nothing valid is. */
export function readTradePresets(): readonly TradePreset[] {
  const raw = storedRaw()
  if (raw !== seenRaw) {
    seenRaw = raw
    seenSet = parsed(raw)
  }
  return seenSet
}

/** Saves the three presets, held to the same rules a read holds them to. */
export function writeTradePresets(next: readonly TradePreset[]): void {
  store(JSON.stringify(checked(next)))
}

/** Back to the shipped presets: nothing saved. */
export function resetTradePresets(): void {
  store(null)
}

function onStorage(event: StorageEvent) {
  // key null: the whole storage was cleared
  if (event.key !== null && event.key !== TRADE_PRESETS_KEY) return
  for (const listener of [...listeners]) listener()
}

/** Calls the listener after every change, here or in another tab. */
export function subscribeTradePresets(listener: () => void): () => void {
  if (listeners.size === 0) window.addEventListener('storage', onStorage)
  listeners.add(listener)
  return () => {
    listeners.delete(listener)
    if (listeners.size === 0) window.removeEventListener('storage', onStorage)
  }
}

/** The saved presets, re-rendering the surface the moment one is edited. */
export function useTradePresets(): readonly TradePreset[] {
  return useSyncExternalStore(subscribeTradePresets, readTradePresets)
}
